import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Avatar,
  useTheme,
} from '@mui/material';
import { Home as HomeIcon, Business as BusinessIcon } from '@mui/icons-material';
import { TenantPermission } from '../types/permissions';

const getRoleColor = (role: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (role) {
    case 'admin':
      return 'error';
    case 'operator':
      return 'warning';
    case 'viewer':
      return 'info';
    default:
      return 'default';
  }
};

interface TenantSelectionViewProps {
  tenants: TenantPermission[];
  onSelect: (tenant: TenantPermission) => void;
  title?: string;
  subtitle?: string;
}

export const TenantSelectionView: React.FC<TenantSelectionViewProps> = ({
  tenants,
  onSelect,
  title = 'Select a tenant',
  subtitle,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
        py: 4,
        px: 2,
      }}
    >
      {(title || subtitle) && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          {title && (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                color: 'text.primary',
                fontFamily: 'JetBrains Mono, Fira Code, monospace',
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 3,
          width: '100%',
        }}
      >
        {tenants.map((tenant) => (
          <Card
            key={tenant.tenant_id}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              background: isDark ? 'rgba(26, 35, 50, 0.7)' : 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(12px)',
              overflow: 'hidden',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                borderColor: theme.palette.primary.main,
                boxShadow: isDark
                  ? `0 12px 40px rgba(0, 240, 255, 0.12)`
                  : `0 12px 40px rgba(0, 144, 178, 0.15)`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.light})`,
                zIndex: 0,
              },
            }}
          >
            <CardActionArea onClick={() => onSelect(tenant)} sx={{ height: '100%' }}>
              <CardContent sx={{ pt: 3.5, pb: 2.5, px: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    <HomeIcon fontSize="medium" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                      {tenant.tenant_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 16 }} color="action" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {tenant.partner_name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Chip
                  label={tenant.role}
                  size="small"
                  color={getRoleColor(tenant.role)}
                  sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};
